import * as React from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { Grid, Header } from 'semantic-ui-react';
import { Field, FieldMetaState } from 'react-final-form';
import {
    normalizers,
    validators,
    RenderField,
    RenderDropdown,
    RenderCheckbox
} from 'forms';

import RoleGate from 'components/RoleGate';
// @ts-ignore
import GroupOptions from 'components/GroupOptions';
// @ts-ignore
import RoleOptions from 'components/RoleOptions';
// @ts-ignore
import GroupDropdownFilterDropdown from 'components/GroupDropdownFilterDropdown';
import FieldValue from 'components/FieldValue';

import { selectUsers } from 'services/people/selectors';
import { AppState } from 'services/types';

import ProfileMessages from './components/ProfileMessages';
import { makeSelectInitialValues } from '../../selectors';
import { PersonFormIntialValues } from '../../types';
import { ProfileFieldName } from '../../constants';

// Normalizers
const { toNull, toArrayIfFalsy } = normalizers;

// Validators
const { composeValidators, isRequired, isEmail, hasLength } = validators;

interface OwnProps {
    personId?: number;
}

interface ConnectedProps {
    initialValues: PersonFormIntialValues;
    users: ReturnType<typeof selectUsers>;
}

type Props = OwnProps & ConnectedProps;

export class ProfileFormComponent extends React.PureComponent<Props> {
    render(): React.ReactElement {
        const { initialValues, users, personId } = this.props;
        const mobileNumberField = (
            <Field
                name={ProfileFieldName.MOBILE_NUMBER}
                component={RenderField as any}
                label="Mobile number"
                type="text"
                placeholder="Enter mobile number"
                parse={personId ? toNull : undefined}
                validate={composeValidators(validators.isMobileNumber)}
            />
        );
        const payrollNumberField = (
            <Field
                name={ProfileFieldName.PAYROLL_NUMBER}
                component={RenderField as any}
                label="Payroll number"
                type="text"
                placeholder="Enter payroll number"
                parse={personId ? toNull : undefined}
            />
        );

        return (
            <Grid>
                <Grid.Column width={8}>
                    <Header as="h3">Profile Details</Header>
                </Grid.Column>
                <Grid.Column textAlign="right" width={8}>
                    <span className="person-form-payroll-text">
                        Payroll No#:{' '}
                        {(initialValues && initialValues.datacomId) || '-'}
                    </span>
                </Grid.Column>
                <Grid.Column mobile={16} tablet={8} computer={8}>
                    <Field
                        name={ProfileFieldName.FIRST_NAME}
                        component={RenderField as any}
                        label="First name"
                        type="text"
                        placeholder="Enter first name"
                        parse={toNull}
                        validate={isRequired}
                    />
                </Grid.Column>
                <Grid.Column mobile={16} tablet={8} computer={8}>
                    <Field
                        name={ProfileFieldName.LAST_NAME}
                        component={RenderField as any}
                        label="Last name"
                        type="text"
                        placeholder="Enter last name"
                        parse={toNull}
                        validate={isRequired}
                    />
                </Grid.Column>
                <RoleGate
                    requiredRoles={['ADMIN']}
                    fallback={
                        <React.Fragment>
                            <Grid.Column mobile={16} tablet={8} computer={8}>
                                {payrollNumberField}
                            </Grid.Column>
                            <Grid.Column mobile={16} tablet={8} computer={8}>
                                {mobileNumberField}
                            </Grid.Column>
                        </React.Fragment>
                    }
                >
                    <Grid.Column mobile={16} tablet={8} computer={8}>
                        <Field
                            name={ProfileFieldName.EMAIL}
                            component={RenderField as any}
                            label="Email"
                            type="text"
                            placeholder="Enter email"
                            validate={composeValidators(
                                isEmail,
                                (
                                    value: string,
                                    record: unknown,
                                    { dirty }: FieldMetaState<string>
                                ) =>
                                    users[value] && dirty
                                        ? 'Email already exists'
                                        : undefined,
                                initialValues && initialValues.email
                                    ? isRequired
                                    : () => {}
                            )}
                        />
                    </Grid.Column>
                    <Grid.Column mobile={16} tablet={8} computer={8}>
                        {mobileNumberField}
                    </Grid.Column>
                    <Grid.Column width={16}>{payrollNumberField}</Grid.Column>
                </RoleGate>
                <Grid.Column width={16}>
                    <GroupOptions>
                        {(options: Record<string, any>): React.ReactElement => (
                            <Field
                                name={ProfileFieldName.GROUPS}
                                label="Groups"
                                placeholder="Add groups here"
                                parse={toArrayIfFalsy}
                                // @ts-ignore
                                component={RenderDropdown}
                                subscription={{
                                    value: true,
                                    touched: true,
                                    error: true
                                }}
                                options={options}
                                multiple
                                renderOptions={
                                    <GroupDropdownFilterDropdown
                                        resetOnNewGroupCreation
                                    />
                                }
                                clearable
                                fluid
                                search
                                selection
                                closeOnChange
                            />
                        )}
                    </GroupOptions>
                </Grid.Column>
                <RoleGate requiredRoles={['ADMIN']}>
                    <FieldValue name={ProfileFieldName.EMAIL}>
                        {(email) =>
                            email ? (
                                <Grid.Column width={16}>
                                    <RoleOptions>
                                        {(
                                            options: Record<string, any>
                                        ): React.ReactElement => (
                                            <Field
                                                name={ProfileFieldName.ROLES}
                                                label="Roles"
                                                placeholder="Add roles here"
                                                parse={toArrayIfFalsy}
                                                component={
                                                    RenderDropdown as any
                                                }
                                                subscription={{
                                                    value: true,
                                                    touched: true,
                                                    error: true
                                                }}
                                                options={options}
                                                validate={hasLength}
                                                multiple
                                                clearable
                                                fluid
                                                search
                                                selection
                                                closeOnChange
                                            />
                                        )}
                                    </RoleOptions>
                                </Grid.Column>
                            ) : null
                        }
                    </FieldValue>
                </RoleGate>
                <Grid.Column width={16}>
                    <Field
                        name={ProfileFieldName.SPRAY_OPERATOR}
                        component={RenderCheckbox as any}
                        label="Spray operator"
                    />
                </Grid.Column>
                <ProfileMessages personId={personId} />
            </Grid>
        );
    }
}

const mapStateToProps = createStructuredSelector<
    AppState,
    OwnProps,
    ConnectedProps
>({
    initialValues: makeSelectInitialValues(),
    users: selectUsers
});

export default connect(mapStateToProps)(ProfileFormComponent);
